import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AdminSurface from '../../../../components/admin/AdminSurface';
import { adminColors, adminTypography } from '../../../../styles/adminDesignTokens';
import { adminTableSx } from '../../../../styles/adminComponentStyles';
import type { StoreLocationRegister, StoreLocation } from '../../../../types/features/storeDetails/locations';

type StoreDetailsLocationRegistersTableProps = {
  error: string;
  loading: boolean;
  locations: StoreLocation[];
  onRetry: () => void;
  onToggleActive: (register: StoreLocationRegister, nextActive: boolean) => void;
  registers: StoreLocationRegister[];
  updatingRegisterId: string;
};

const StoreDetailsLocationRegistersTable: React.FC<StoreDetailsLocationRegistersTableProps> = ({
  error,
  loading,
  locations,
  onRetry,
  onToggleActive,
  registers,
  updatingRegisterId,
}) => {
  const locationNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    locations.forEach((location) => {
      map.set(location.id, location.location_name);
    });
    return map;
  }, [locations]);

  return (
    <AdminSurface sx={{ overflow: 'hidden' }}>
      <Box sx={{ backgroundColor: '#F8FBFF', borderBottom: `1px solid ${adminColors.divider}`, px: 2, py: 2.5 }}>
        <Typography sx={{ ...adminTypography.sectionTitle, color: '#2C6CA0' }}>
          POS Register Mapping
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={34} />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Typography sx={{ ...adminTypography.body, color: adminColors.danger }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={onRetry}
              sx={{
                backgroundColor: adminColors.primary,
                boxShadow: 'none',
                width: 'fit-content',
                '&:hover': { backgroundColor: adminColors.primaryHover },
              }}
            >
              Retry
            </Button>
          </Box>
        ) : registers.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }}>
              No register locations found for this store.
            </Typography>
          </Box>
        ) : (
          <TableContainer
            sx={{
              border: 'none',
              borderRadius: 0,
              overflowX: 'auto',
            }}
          >
            <Table
              size="small"
              aria-label="Register and location map"
              sx={{
                ...adminTableSx,
                minWidth: 480,
                '& .MuiTableCell-root': {
                  padding: '8px 12px !important',
                },
              }}
            >
              <TableHead sx={{ '& th': { padding: '8px 12px !important', textTransform: 'uppercase' } }}>
                <TableRow>
                  <TableCell sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                    Register ID
                  </TableCell>
                  <TableCell sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                    Assigned Location
                  </TableCell>
                  <TableCell sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {registers.map((register) => {
                  const isUpdating = updatingRegisterId === register.id;

                  return (
                    <TableRow key={register.id} hover>
                      <TableCell sx={{ ...adminTypography.body, color: adminColors.text }}>
                        {register.register_id}
                      </TableCell>
                      <TableCell sx={{ ...adminTypography.body, color: adminColors.text }}>
                        {locationNameById.get(register.store_location_id) || 'Unknown location'}
                      </TableCell>
                      <TableCell sx={{ ...adminTypography.body, color: adminColors.text }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Switch
                            size="small"
                            checked={register.active}
                            disabled={isUpdating}
                            onChange={(_event, nextChecked) => {
                              void onToggleActive(register, nextChecked);
                            }}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: adminColors.primary,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: adminColors.primary,
                              },
                            }}
                          />
                          <Typography sx={{ ...adminTypography.body, color: adminColors.text }}>
                            {isUpdating ? 'Updating...' : register.active ? 'Enabled' : 'Disabled'}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </AdminSurface>
  );
};

export default React.memo(StoreDetailsLocationRegistersTable);
